import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Play, CheckCircle, XCircle } from 'lucide-react';

interface Slide {
  id: number;
  title: string;
  titleFr: string;
  content: React.ReactNode;
}

const SlidePresentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [answers, setAnswers] = useState<{[key: string]: string}>({});
  const [showResults, setShowResults] = useState<{[key: string]: boolean}>({});

  const checkAnswer = (questionId: string, userAnswer: string, correctAnswer: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: userAnswer }));
    setShowResults(prev => ({ ...prev, [questionId]: true }));
  };

  const slides: Slide[] = [
    // Title Slide
    {
      id: 1,
      title: "Using 'Going To' in English",
      titleFr: "L'utilisation de 'Going To' en anglais",
      content: (
        <div className="text-center space-y-8">
          <div className="flex justify-center mb-6">
            <img 
              src="/images/english_french_flags_2.png" 
              alt="English and French flags" 
              className="w-32 h-32 object-contain"
            />
          </div>
          <h1 className="text-4xl font-bold text-blue-600 mb-4">
            Using "Going To" in English
          </h1>
          <h2 className="text-3xl font-semibold text-red-500 mb-6">
            L'utilisation de "Going To" en anglais
          </h2>
          <div className="bg-gradient-to-r from-blue-100 to-red-100 p-6 rounded-lg">
            <p className="text-lg text-gray-700 mb-2">
              <strong>English:</strong> A beginner's guide to expressing future plans and predictions
            </p>
            <p className="text-lg text-gray-700">
              <strong>Français:</strong> Un guide pour débutants sur l'expression des projets futurs et des prédictions
            </p>
          </div>
        </div>
      )
    },

    // Introduction Slide
    {
      id: 2,
      title: "What is 'Going To'?",
      titleFr: "Qu'est-ce que 'Going To'?",
      content: (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="text-blue-700 flex items-center gap-2">
                  🇬🇧 English
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg mb-4">
                  "Going to" is used to express:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li>• Future plans and intentions</li>
                  <li>• Predictions based on evidence</li>
                  <li>• Things that will definitely happen</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="text-red-700 flex items-center gap-2">
                  🇫🇷 Français
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg mb-4">
                  "Going to" s'utilise pour exprimer:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li>• Les projets et intentions futurs</li>
                  <li>• Les prédictions basées sur des preuves</li>
                  <li>• Les choses qui vont certainement arriver</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="bg-yellow-100 p-4 rounded-lg border-l-4 border-yellow-500">
            <p className="text-gray-800">
              <strong>Key Point / Point clé:</strong> In French, we often use "aller + infinitive" as the equivalent.
            </p>
          </div>
        </div>
      )
    },

    // Structure Slide
    {
      id: 3,
      title: "Structure / Formation",
      titleFr: "Structure / Formation",
      content: (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-blue-200">
              <CardHeader>
                <CardTitle className="text-blue-700">🇬🇧 English Structure</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-100 p-4 rounded-lg">
                  <p className="font-mono text-lg text-center">
                    Subject + <span className="text-blue-600 font-bold">am/is/are</span> + 
                    <span className="text-green-600 font-bold"> going to</span> + 
                    <span className="text-purple-600 font-bold"> verb</span>
                  </p>
                </div>
                <div className="space-y-2">
                  <p><strong>Positive:</strong> I am going to study</p>
                  <p><strong>Negative:</strong> I am not going to study</p>
                  <p><strong>Question:</strong> Are you going to study?</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="text-red-700">🇫🇷 French Equivalent</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-red-100 p-4 rounded-lg">
                  <p className="font-mono text-lg text-center">
                    Sujet + <span className="text-red-600 font-bold">aller</span> + 
                    <span className="text-purple-600 font-bold"> infinitif</span>
                  </p>
                </div>
                <div className="space-y-2">
                  <p><strong>Positif:</strong> Je vais étudier</p>
                  <p><strong>Négatif:</strong> Je ne vais pas étudier</p>
                  <p><strong>Question:</strong> Vas-tu étudier?</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )
    },

    // Future Plans Slide
    {
      id: 4,
      title: "Future Plans & Intentions",
      titleFr: "Projets et Intentions Futurs",
      content: (
        <div className="space-y-6">
          <div className="flex justify-center mb-4">
            <img 
              src="/images/future_plans_1.jpeg" 
              alt="Future planning" 
              className="w-48 h-32 object-cover rounded-lg"
            />
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-blue-200">
              <CardHeader>
                <CardTitle className="text-blue-700">🇬🇧 English Examples</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-blue-50 p-3 rounded">
                  <p><strong>I'm going to visit Paris next month.</strong></p>
                  <p className="text-sm text-gray-600">Personal plan/intention</p>
                </div>
                <div className="bg-blue-50 p-3 rounded">
                  <p><strong>We're going to buy a new car.</strong></p>
                  <p className="text-sm text-gray-600">Decided plan</p>
                </div>
                <div className="bg-blue-50 p-3 rounded">
                  <p><strong>She's going to learn French.</strong></p>
                  <p className="text-sm text-gray-600">Future intention</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="text-red-700">🇫🇷 Exemples Français</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-red-50 p-3 rounded">
                  <p><strong>Je vais visiter Paris le mois prochain.</strong></p>
                  <p className="text-sm text-gray-600">Projet personnel/intention</p>
                </div>
                <div className="bg-red-50 p-3 rounded">
                  <p><strong>Nous allons acheter une nouvelle voiture.</strong></p>
                  <p className="text-sm text-gray-600">Plan décidé</p>
                </div>
                <div className="bg-red-50 p-3 rounded">
                  <p><strong>Elle va apprendre le français.</strong></p>
                  <p className="text-sm text-gray-600">Intention future</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )
    },

    // Predictions Slide
    {
      id: 5,
      title: "Predictions Based on Evidence",
      titleFr: "Prédictions Basées sur des Preuves",
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-orange-100 to-yellow-100 p-4 rounded-lg">
            <p className="text-center text-lg font-semibold text-gray-800">
              When we can see evidence that something will happen / Quand nous voyons des preuves qu'quelque chose va arriver
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-blue-200">
              <CardHeader>
                <CardTitle className="text-blue-700">🇬🇧 English Examples</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-blue-50 p-3 rounded">
                  <p><strong>Look at those clouds! It's going to rain.</strong></p>
                  <p className="text-sm text-gray-600">Evidence: dark clouds</p>
                </div>
                <div className="bg-blue-50 p-3 rounded">
                  <p><strong>He's going to fall! (wobbling on ladder)</strong></p>
                  <p className="text-sm text-gray-600">Evidence: unstable position</p>
                </div>
                <div className="bg-blue-50 p-3 rounded">
                  <p><strong>The team is going to win. (leading 3-0)</strong></p>
                  <p className="text-sm text-gray-600">Evidence: current score</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="text-red-700">🇫🇷 Exemples Français</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-red-50 p-3 rounded">
                  <p><strong>Regarde ces nuages! Il va pleuvoir.</strong></p>
                  <p className="text-sm text-gray-600">Preuve: nuages sombres</p>
                </div>
                <div className="bg-red-50 p-3 rounded">
                  <p><strong>Il va tomber! (vacille sur l'échelle)</strong></p>
                  <p className="text-sm text-gray-600">Preuve: position instable</p>
                </div>
                <div className="bg-red-50 p-3 rounded">
                  <p><strong>L'équipe va gagner. (mène 3-0)</strong></p>
                  <p className="text-sm text-gray-600">Preuve: score actuel</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )
    },

    // Negative and Questions Slide
    {
      id: 6,
      title: "Negative Forms & Questions",
      titleFr: "Formes Négatives et Questions",
      content: (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-blue-200">
              <CardHeader>
                <CardTitle className="text-blue-700">🇬🇧 English Forms</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold text-green-600 mb-2">Negative:</h4>
                  <div className="bg-blue-50 p-3 rounded space-y-1">
                    <p>I'm <strong>not going to</strong> work tomorrow.</p>
                    <p>She <strong>isn't going to</strong> come.</p>
                    <p>They <strong>aren't going to</strong> help us.</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-purple-600 mb-2">Questions:</h4>
                  <div className="bg-blue-50 p-3 rounded space-y-1">
                    <p><strong>Are you going to</strong> study tonight?</p>
                    <p><strong>Is he going to</strong> call you?</p>
                    <p><strong>What are you going to</strong> do?</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="text-red-700">🇫🇷 Formes Françaises</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold text-green-600 mb-2">Négatif:</h4>
                  <div className="bg-red-50 p-3 rounded space-y-1">
                    <p>Je <strong>ne vais pas</strong> travailler demain.</p>
                    <p>Elle <strong>ne va pas</strong> venir.</p>
                    <p>Ils <strong>ne vont pas</strong> nous aider.</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-purple-600 mb-2">Questions:</h4>
                  <div className="bg-red-50 p-3 rounded space-y-1">
                    <p><strong>Vas-tu</strong> étudier ce soir?</p>
                    <p><strong>Va-t-il</strong> t'appeler?</p>
                    <p><strong>Qu'est-ce que tu vas</strong> faire?</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )
    },

    // Practice Exercise 1
    {
      id: 7,
      title: "Practice Exercise 1",
      titleFr: "Exercice Pratique 1",
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-purple-600 mb-4">Translation Practice / Pratique de Traduction</h3>
          </div>
          
          <div className="space-y-4">
            <Card className="border-purple-200">
              <CardContent className="p-6">
                <p className="text-lg mb-4"><strong>Translate to English / Traduisez en anglais:</strong></p>
                <p className="text-xl text-blue-600 mb-4">"Je vais regarder un film ce soir."</p>
                
                <div className="space-y-2">
                  <Button 
                    variant={answers.q1 === 'correct' ? 'default' : 'outline'}
                    className="w-full text-left justify-start"
                    onClick={() => checkAnswer('q1', 'correct', 'correct')}
                  >
                    I'm going to watch a movie tonight.
                    {showResults.q1 && answers.q1 === 'correct' && <CheckCircle className="ml-2 h-4 w-4 text-green-500" />}
                  </Button>
                  <Button 
                    variant={answers.q1 === 'wrong1' ? 'destructive' : 'outline'}
                    className="w-full text-left justify-start"
                    onClick={() => checkAnswer('q1', 'wrong1', 'correct')}
                  >
                    I will watch a movie tonight.
                    {showResults.q1 && answers.q1 === 'wrong1' && <XCircle className="ml-2 h-4 w-4 text-red-500" />}
                  </Button>
                  <Button 
                    variant={answers.q1 === 'wrong2' ? 'destructive' : 'outline'}
                    className="w-full text-left justify-start"
                    onClick={() => checkAnswer('q1', 'wrong2', 'correct')}
                  >
                    I watch a movie tonight.
                    {showResults.q1 && answers.q1 === 'wrong2' && <XCircle className="ml-2 h-4 w-4 text-red-500" />}
                  </Button>
                </div>
                
                {showResults.q1 && (
                  <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>Explanation:</strong> "Je vais" translates to "I'm going to" when expressing future plans.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )
    },

    // Practice Exercise 2
    {
      id: 8,
      title: "Practice Exercise 2",
      titleFr: "Exercice Pratique 2",
      content: (
        <div className="space-y-6">
          <Card className="border-green-200">
            <CardContent className="p-6">
              <p className="text-lg mb-4"><strong>Complete the sentence / Complétez la phrase:</strong></p>
              <p className="text-xl mb-4">Look at those dark clouds! It _______ rain soon.</p>
              
              <div className="space-y-2">
                <Button 
                  variant={answers.q2 === 'correct' ? 'default' : 'outline'}
                  className="w-full text-left justify-start"
                  onClick={() => checkAnswer('q2', 'correct', 'correct')}
                >
                  is going to
                  {showResults.q2 && answers.q2 === 'correct' && <CheckCircle className="ml-2 h-4 w-4 text-green-500" />}
                </Button>
                <Button 
                  variant={answers.q2 === 'wrong1' ? 'destructive' : 'outline'}
                  className="w-full text-left justify-start"
                  onClick={() => checkAnswer('q2', 'wrong1', 'correct')}
                >
                  will
                  {showResults.q2 && answers.q2 === 'wrong1' && <XCircle className="ml-2 h-4 w-4 text-red-500" />}
                </Button>
                <Button 
                  variant={answers.q2 === 'wrong2' ? 'destructive' : 'outline'}
                  className="w-full text-left justify-start"
                  onClick={() => checkAnswer('q2', 'wrong2', 'correct')}
                >
                  goes to
                  {showResults.q2 && answers.q2 === 'wrong2' && <XCircle className="ml-2 h-4 w-4 text-red-500" />}
                </Button>
              </div>
              
              {showResults.q2 && (
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Explanation:</strong> We use "going to" for predictions based on present evidence (dark clouds).
                  </p>
                  <p className="text-sm text-gray-700 mt-1">
                    <strong>French:</strong> "Il va pleuvoir bientôt."
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )
    },

    // Summary Slide
    {
      id: 9,
      title: "Summary / Résumé",
      titleFr: "Summary / Résumé",
      content: (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <img 
              src="/images/grammar_lesson_2.jpeg" 
              alt="Grammar summary" 
              className="w-48 h-32 object-cover rounded-lg mx-auto"
            />
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="text-blue-700">🇬🇧 Key Points</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-2">
                  <Badge variant="secondary">1</Badge>
                  <p>Use "going to" for future plans and intentions</p>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="secondary">2</Badge>
                  <p>Use "going to" for predictions with evidence</p>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="secondary">3</Badge>
                  <p>Structure: am/is/are + going to + verb</p>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="secondary">4</Badge>
                  <p>Negative: am/is/are + not + going to + verb</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="text-red-700">🇫🇷 Points Clés</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-2">
                  <Badge variant="secondary">1</Badge>
                  <p>Utilisez "aller + infinitif" pour les projets futurs</p>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="secondary">2</Badge>
                  <p>Utilisez "aller + infinitif" pour les prédictions avec preuves</p>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="secondary">3</Badge>
                  <p>Structure: sujet + aller + infinitif</p>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="secondary">4</Badge>
                  <p>Négatif: sujet + ne + aller + pas + infinitif</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="bg-gradient-to-r from-green-100 to-blue-100 p-6 rounded-lg text-center">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Congratulations! / Félicitations!</h3>
            <p className="text-lg text-gray-700">
              You've learned how to use "going to" in English and its French equivalent!
            </p>
            <p className="text-lg text-gray-700">
              Vous avez appris à utiliser "going to" en anglais et son équivalent français!
            </p>
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-red-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold text-gray-800">
            Bilingual Grammar Lesson
          </h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              Slide {currentSlide + 1} of {slides.length}
            </span>
            <div className="flex gap-1">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentSlide ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Slide Content */}
        <Card className="min-h-[600px] shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-red-500 text-white">
            <CardTitle className="text-center">
              <div className="text-xl">{slides[currentSlide].title}</div>
              <div className="text-lg opacity-90">{slides[currentSlide].titleFr}</div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            {slides[currentSlide].content}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between items-center mt-8">
          <Button 
            onClick={prevSlide} 
            disabled={currentSlide === 0}
            variant="outline"
            className="flex items-center gap-2"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous / Précédent
          </Button>
          
          <div className="flex gap-2">
            <Button 
              onClick={() => goToSlide(0)}
              variant="ghost"
              size="sm"
            >
              <Play className="h-4 w-4" />
            </Button>
          </div>

          <Button 
            onClick={nextSlide} 
            disabled={currentSlide === slides.length - 1}
            className="flex items-center gap-2"
          >
            Next / Suivant
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SlidePresentation;